import type { JiraAdminAgentFieldOptionsStepPayload } from '@atlassian/ai-mate-jira-admin-agent-api/jira-admin-agent';

import type { PlanNode, StepNode } from './types';

const collectSteps = (plan: PlanNode): StepNode[] => {
	return plan.steps.concat(plan.plans.flatMap(collectSteps));
};

const hasMatchingFieldName = (steps: StepNode[]): boolean => {
	const fieldNames = new Set(
		steps.map((step) => (step.payload as JiraAdminAgentFieldOptionsStepPayload['payload']).fieldId),
	);

	return fieldNames.size === 1;
};

// When a plan tree contains exactly one step, or all steps relate to field options for the same field,
// in order to avoid a single expandable section on the action card we combine the payload of the sub-plan or intent,
// with the blast radius of the plan
export const collapseSingleStepPlan = (
	topLevelPlan: PlanNode | undefined,
): PlanNode | undefined => {
	if (!topLevelPlan) {
		return topLevelPlan;
	}

	const allSteps = collectSteps(topLevelPlan);
	const isSingleStep = allSteps.length === 1;
	const isFieldOptionsForSameField =
		allSteps.every((step) => step.actionSubject === 'field-options') &&
		hasMatchingFieldName(allSteps);

	if (isSingleStep || isFieldOptionsForSameField) {
		return {
			actionType: 'plan',
			id: topLevelPlan.id,
			payload: {
				...(topLevelPlan.plans[0] ?? topLevelPlan).payload,
				blastRadius: topLevelPlan.payload.blastRadius,
				summary: topLevelPlan.payload.summary,
			},
			plans: [],
			steps: allSteps,
		};
	}

	return topLevelPlan;
};
